'use client';

import { ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { css } from 'styled-system/css';

const OpenVideoContext = createContext<() => void>(() => {});

// Chakra `Modal size="full"` (ModalOverlay bg black + ModalContent bg black, radius 0, m 0, 100dvh,
// centred) as a native modal <dialog>: top layer, inert background, Escape closes, focus returns
// to the trigger. Hidden unless [open].
const dialogClass = css({
  display: 'none',
  _open: { display: 'flex' },
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'fixed',
  inset: 0,
  w: '100vw',
  maxW: '100vw',
  h: '100dvh',
  maxH: '100dvh',
  m: 0,
  p: 0,
  border: 'none',
  borderRadius: 0,
  bg: 'black',
  color: 'inherit',
  zIndex: 'modal',
  _backdrop: { bg: 'black' },
});

// ModalCloseButton (v2 CloseButton md + the section's overrides: color white, top/right 6, 10×10, fontSize lg).
const closeButtonClass = css({
  position: 'absolute',
  top: 6,
  right: 6,
  w: 10,
  h: 10,
  fontSize: 'lg',
  zIndex: 1,
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  borderRadius: 'md',
  bg: 'transparent',
  border: 'none',
  cursor: 'pointer',
  outline: 'none',
  transitionProperty: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
  transitionDuration: 'normal',
  _hover: { bg: 'blackAlpha.100' },
  _active: { bg: 'blackAlpha.200' },
  _focusVisible: { boxShadow: 'outline' },
});

const bodyClass = css({
  flex: 1,
  p: { base: 4, md: 12 },
  w: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" focusable="false" aria-hidden width="1em" height="1em">
    <path
      fill="currentColor"
      d="M.439,21.44a1.5,1.5,0,0,0,2.122,2.121L11.823,12.3a.25.25,0,0,1,.354,0l9.262,9.263a1.5,1.5,0,1,0,2.122-2.121L12.3,12.177a.25.25,0,0,1,0-.354l9.263-9.262A1.5,1.5,0,0,0,21.439.44L12.177,9.7a.25.25,0,0,1-.354,0L2.561.44A1.5,1.5,0,0,0,.439,2.561L9.7,11.823a.25.25,0,0,1,0,.354Z"
    />
  </svg>
);

type ModalProps = { videoUrl: string; children: ReactNode };

/**
 * Client island of CyberRiskCaseStudySection: owns the popup state; the section content stays
 * server children, the play buttons (`PlayVideoButton`) open it through context. The YouTube
 * iframe exists only while open (closing stops playback).
 */
export const CyberRiskVideoModal = ({ videoUrl, children }: ModalProps) => {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const openVideo = useCallback(() => setOpen(true), []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    if (!open) {
      if (dialog.open) dialog.close();
      return undefined;
    }
    if (!dialog.open) dialog.showModal();
    // Chakra's Modal locks page scroll while open.
    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => {
      root.style.overflow = previousOverflow;
    };
  }, [open]);

  return (
    <OpenVideoContext value={openVideo}>
      {children}
      <dialog ref={dialogRef} className={dialogClass} onClose={() => setOpen(false)}>
        <button type="button" aria-label="Close" className={closeButtonClass} onClick={() => setOpen(false)}>
          <CloseIcon />
        </button>
        <div className={bodyClass}>
          <div className={css({ position: 'relative', w: '100%', aspectRatio: '16/9' })}>
            {open && (
              <iframe
                src={`${videoUrl}&autoplay=1`}
                title="Case study video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
              />
            )}
          </div>
        </div>
      </dialog>
    </OpenVideoContext>
  );
};

type ButtonProps = { className?: string; children: ReactNode };

/** A "Play video" trigger (server children = poster / icon) inside `CyberRiskVideoModal`. */
export const PlayVideoButton = ({ className, children }: ButtonProps) => {
  const openVideo = useContext(OpenVideoContext);
  return (
    <button type="button" aria-label="Play video" className={className} onClick={openVideo}>
      {children}
    </button>
  );
};
